import { cloneDeep, isEmpty, reject } from 'lodash';

import { useMessagesCall } from '@/composables/domains/chat/useMessagesCall';
import { useAudioStore } from '@/stores/audio';
import { useSocketStore } from '@/stores/socket';
import { useUserStore } from '@/stores/user';
import { LanguageCode } from '@/types/common';
import { IPastChatMessage } from '@/types/socket';
import {
  InboundMessageAL,
  InboundMessageCC,
  InboundMessageCD,
  InboundMessageCN,
  InboundMessageTR,
} from '@/types/socket/inbound';
import {
  ChatMessage,
  ChatMessageAudio,
  ChatMessageBody,
  ChatMessageContent,
  ChatMessageState,
  InboundMessagePayload,
  MessagePosition,
  Attendee,
} from '@/types/socket/message';
import { MessageCategory } from '@/types/socket/message';
import { getTypedKeys } from '@/utils/common/getTypedKeys';
import { LANGUAGES_WITHOUT_SPACES } from '@/utils/constants/common';
import { INITIAL_MESSAGE_TEMPLATE, INITIAL_STATUS } from '@/utils/constants/socket';
import { base64UrlSafeToString } from '@/utils/encryptor.js';
import useQueue from '@/utils/useQueue';

export const useMessageStore = defineStore('message', () => {
  const userStore = useUserStore();
  const socketStore = useSocketStore();
  const audioStore = useAudioStore();

  /* state */
  const status = reactive<ChatMessageState>(cloneDeep(INITIAL_STATUS));

  const { upsertLegacyMessage, deleteMessageLegacy, updateLegacyMessageNickname } = useMessagesCall();
  const messageQueue = useQueue();

  const messageMap = ref<Record<string, ChatMessage>>({});
  const messageMapIds = ref<string[]>([]);

  const messages = computed<ChatMessage[]>(() => {
    return messageMapIds.value.map((id) => messageMap.value[id] || {});
  });

  const activeAttendees = computed<Attendee[]>(() => {
    const attendees = Object.values(status.attendees);
    return attendees.filter(({ isActive }) => isActive);
  });

  /* utils */
  const getMessageId = (msg: Pick<InboundMessagePayload, 'chatLogId' | 'socketId'>, category: MessageCategory) => {
    return category === 'DIRECT_MESSAGE' ? msg.socketId || msg.chatLogId : msg.chatLogId;
  };

  const setMessageMapId = (id: string, position: MessagePosition) => {
    if (!id || messageMapIds.value.includes(id)) {
      return;
    }

    if (position === 'prepend') {
      messageMapIds.value.unshift(id);
    } else {
      messageMapIds.value.push(id);
    }
  };

  /* actions */
  /** Messages **/
  const upsertMessage = (
    msg: InboundMessagePayload,
    category: Extract<MessageCategory, 'MESSAGE' | 'DIRECT_MESSAGE'>,
    position: MessagePosition = 'append'
  ) => {
    messageQueue.add(async () => {
      const existingMsg = messageMap.value[getMessageId(msg, category)];
      if (existingMsg) {
        await updateMessage(existingMsg, msg);
      } else {
        await insertMessage(msg, category, position);
      }
    });
  };

  const insertMessage = async (msg: InboundMessagePayload, category: MessageCategory, position: MessagePosition) => {
    const emptyBody: ChatMessageBody = {
      languageCodes: [],
      contents: [],
      audios: [],
    };
    const filledBody: ChatMessageBody = {
      languageCodes: [msg.languageCode],
      contents: getUpdatedMessageContent([], msg),
      audios: getUpdatedMessageAudio([], msg),
    };

    const message: ChatMessage = {
      category,
      chatId: msg.chatId,
      sentAt: msg.sentAt,
      chatLogId: msg.chatLogId,
      senderId: msg.senderId,
      isOwn: msg.senderId === userStore.userId,
      nickname: base64UrlSafeToString(msg.nickname) || getAttendeeNickname(msg.senderId),
      src: msg.dataFlag === 'src' ? filledBody : cloneDeep(emptyBody),
      tgt: msg.dataFlag === 'tgt' ? filledBody : cloneDeep(emptyBody),
    };
    const id = getMessageId(msg, category);
    setMessageMapId(id, position);
    messageMap.value[id] = message;
    // FIXME: 소켓리팩 2차 개선때, 삭제필요 (message Store의 messages 만으로 유지되어야합니다.) (26/1/26)
    upsertLegacyMessage(message, position);

    if (msg.status === 'AUDIO_COMPLETED' && msg.dataFlag === 'tgt' && audioStore.isSoundOn && !message.isOwn) {
      await setAutoAudioPlaylist(msg);
    }
  };

  const updateMessage = async (existingMsg: ChatMessage, newMsg: InboundMessagePayload) => {
    const targetBody = newMsg.dataFlag === 'src' ? existingMsg.src : existingMsg.tgt;
    // FIXME: 소켓리팩 2차 개선때, 삭제필요 (message Store의 messages 만으로 유지되어야합니다.) (26/1/26)
    const pastChatLogId = existingMsg.chatLogId;

    targetBody.contents = getUpdatedMessageContent(targetBody.contents, newMsg);
    targetBody.audios = getUpdatedMessageAudio(targetBody.audios, newMsg);
    targetBody.languageCodes = getUpdatedLanguageCode(targetBody.languageCodes, newMsg.languageCode);

    existingMsg = {
      ...existingMsg,
      chatLogId: newMsg.chatLogId,
      nickname:
        getAttendeeNickname(existingMsg.senderId) || existingMsg.nickname || base64UrlSafeToString(newMsg.nickname),
    };

    if (
      newMsg.status === 'AUDIO_COMPLETED' &&
      newMsg.dataFlag === 'tgt' &&
      audioStore.isSoundOn &&
      !existingMsg.isOwn
    ) {
      await setAutoAudioPlaylist(newMsg);
    }

    // FIXME: 소켓리팩 2차 개선때, 삭제필요 (message Store의 messages 만으로 유지되어야합니다.) (26/1/26)
    upsertLegacyMessage({ ...existingMsg, pastChatLogId });
  };

  const setAutoAudioPlaylist = async (msg: InboundMessageTR) => {
    const newAudio = {
      callLogId: msg.chatLogId,
      chatId: msg.chatId,
      chatSeqSubId: msg.chatSubId,
      voiceKey: msg.voiceKey,
      logFlag: msg.logFlag || '',
    };

    await audioStore.addToPlaylistAutoQueue({
      ...newAudio,
      idAudio: audioStore.getIdentifierAudio(newAudio),
      chatId: status.selectedChatId,
    });
  };

  const getUpdatedMessageContent = (
    contents: ChatMessageContent[],
    newMsg: InboundMessagePayload
  ): ChatMessageContent[] => {
    if (!['INCOMPLETED', 'FIXED', 'SENTENCE_COMPLETED'].includes(newMsg.status)) {
      return contents;
    }

    const contentIndex = contents.findIndex((item) => item.chatSubId === newMsg.chatSubId);
    return [
      ...reject(contents, (_, index) => index === contentIndex),
      getMessageContent(contentIndex !== -1 ? contents[contentIndex] : {}, newMsg),
    ];
  };

  const getUpdatedMessageAudio = (audios: ChatMessageAudio[], newMsg: InboundMessagePayload): ChatMessageAudio[] => {
    if (newMsg.status !== 'AUDIO_COMPLETED' || !newMsg.voiceKey) {
      return audios;
    }
    return [...audios, getMessageAudio(newMsg)];
  };

  const getUpdatedLanguageCode = (languageCodes: LanguageCode[], newLanguageCode: LanguageCode): LanguageCode[] => {
    return languageCodes.includes(newLanguageCode) ? languageCodes : [...languageCodes, newLanguageCode];
  };

  const resetMessageStore = () => {
    messageMap.value = {};
    messageMapIds.value = [];

    getTypedKeys(INITIAL_STATUS).forEach((key) => {
      patchStatus(key, cloneDeep(INITIAL_STATUS[key]));
    });
  };

  // [ALL] 메시지를 생성합니다.
  const createMessage = (
    msg: Partial<InboundMessagePayload>,
    category: Exclude<MessageCategory, 'MESSAGE' | 'DIRECT_MESSAGE'>
  ) => {
    messageQueue.add(async () => {
      const message = {
        ...INITIAL_MESSAGE_TEMPLATE,
        ...msg,
      } as InboundMessagePayload;

      if (!messageMap.value[message.chatLogId]) {
        await insertMessage(message, category, 'append');
      }
    });
  };

  // [EM] 다른 사용자가 수정한 메시지를 수정합니다.
  const editMessage = (chatLogId: string, msg: Partial<InboundMessagePayload>) => {
    messageQueue.add(() => {
      const existingMsg = messageMap.value[chatLogId];
      if (
        !existingMsg ||
        !existingMsg[msg.dataFlag === 'src' ? 'src' : 'tgt'].languageCodes.includes(msg.languageCode)
      ) {
        return;
      }

      const targetBody = msg.dataFlag === 'src' ? existingMsg.src : existingMsg.tgt;
      const targetIndex = targetBody.contents.findIndex((item) => item.chatSubId === msg.chatSubId);
      if (targetIndex !== -1) {
        targetBody.contents[targetIndex] = {
          ...targetBody.contents[targetIndex],
          completeText: msg.completeText || '',
        };
        upsertLegacyMessage(existingMsg, 'append');
      }
    });
  };

  const deleteMessage = (chatLogId: string) => {
    messageQueue.add(() => {
      if (!chatLogId || !messageMap.value[chatLogId]) {
        return;
      }

      delete messageMap.value[chatLogId];
      const idx = messageMapIds.value.indexOf(chatLogId);
      if (idx !== -1) {
        messageMapIds.value.splice(idx, 1);
      }

      // FIXME: 소켓리팩 2차 개선때, 삭제필요 (message Store의 messages 만으로 유지되어야합니다.) (26/1/26)
      deleteMessageLegacy(chatLogId);
    });
  };

  const savePastMessage = (pastMessages: IPastChatMessage[], chatId: string) => {
    const convertMessage = (msg, bodyMsg, dataFlag): InboundMessagePayload => {
      return {
        ...INITIAL_MESSAGE_TEMPLATE,
        chatId,
        dataFlag,
        nickname: base64UrlSafeToString(msg.nickname),
        sentAt: msg.sentAt,
        senderId: msg.senderId,
        chatLogId: msg.callLogId,
        socketId: msg.callLogId,
        languageCode: bodyMsg.languageCode,
        chatSubId: bodyMsg.subId,
        completeText: bodyMsg.message,
        incompleteText: '',
        voiceKey: '',
        isFastInterpretationOn: '0',
        status: 'SENTENCE_COMPLETED',
      };
    };

    pastMessages.forEach((msg) => {
      if (!messageMap.value[msg.callLogId]) {
        msg.sourceChatLogSubResponses.forEach((bodyMsg) => {
          const message: InboundMessagePayload = convertMessage(msg, bodyMsg, 'src');
          upsertMessage(message, msg.isText ? 'DIRECT_MESSAGE' : 'MESSAGE', 'prepend');
        });
        msg.targetChatLogSubResponses.forEach((bodyMsg) => {
          const message: InboundMessagePayload = convertMessage(msg, bodyMsg, 'tgt');
          upsertMessage(message, msg.isText ? 'DIRECT_MESSAGE' : 'MESSAGE', 'prepend');
        });
      }
    });
  };

  // 메시지 컨텐츠
  const getMessageContent = (
    existingMsg: Partial<ChatMessageContent>,
    msg: InboundMessagePayload
  ): ChatMessageContent => {
    const splitter = isEmpty(existingMsg) || LANGUAGES_WITHOUT_SPACES.includes(msg.languageCode) ? '' : ' ';

    const { completeText = '', incompleteText = '' } = {
      INCOMPLETED: {
        completeText: existingMsg.completeText,
        incompleteText: splitter + msg.incompleteText,
      },
      FIXED: {
        completeText: (existingMsg.completeText || '') + splitter + msg.completeText,
        incompleteText: splitter + msg.incompleteText,
      },
      SENTENCE_COMPLETED: {
        completeText: (existingMsg.completeText || '') + splitter + msg.completeText,
        incompleteText: '',
      },
    }[msg.status] || {
      completeText: existingMsg.completeText || '',
      incompleteText: existingMsg.incompleteText || '',
    };

    return {
      ...existingMsg,
      completeText,
      incompleteText,
      chatSubId: msg.chatSubId,
      status: msg.status,
      languageCode: msg.languageCode,
      nickname: msg.nickname || existingMsg.nickname || '',
      ...(msg.dmState && { dmState: msg.dmState }),
      ...(msg.dmTimer && { dmTimer: msg.dmTimer }),
    };
  };

  // 메시지 오디오
  const getMessageAudio = (msg: InboundMessagePayload): ChatMessageAudio => {
    return {
      chatId: msg.chatId,
      chatLogId: msg.chatLogId,
      chatSubId: msg.chatSubId,
      voiceKey: msg.voiceKey,
    };
  };
  // 참석자 닉네임 반환
  const getAttendeeNickname = (senderId: string): string => {
    const { nickname = '' } = status.attendees[senderId] || {};
    return nickname;
  };

  /** Status **/
  // 메시지 상태값을 변경합니다.
  const patchStatus = <Key extends keyof typeof status>(key: Key, value: (typeof status)[Key]) => {
    if (key in status) {
      status[key] = value;
    }
    // FIXME: 소켓리팩 2차 개선때, 삭제필요 (message Store의 상태값으로만 유지되어야합니다.) (26/1/26)
    patchLegacyStatus(key, value);
  };

  const patchLegacyStatus = (key, value) => {
    const socketStoreRefs = storeToRefs(socketStore);
    if (socketStoreRefs[key]) {
      socketStoreRefs[key].value = value;
      return;
    }
    const audioStoreRefs = storeToRefs(audioStore);
    if (audioStoreRefs[key]) {
      audioStoreRefs[key].value = value;
      return;
    }
  };

  // [CN] 메시지에 저장되어 있는 닉네임들을 전부 변경합니다.
  const updateMessageNicknames = (msg: InboundMessageCN) => {
    // 01. 참석자 닉네임을 변경합니다.
    const existing = status.attendees[msg.userId ?? ''];
    if (existing) {
      existing.nickname = base64UrlSafeToString(msg.nickname);
    }

    // 02. 메시지에 저장되어 있는 닉네임들을 전부 변경합니다.
    if (msg.isAll) {
      messages.value.forEach((message) => {
        if (message.senderId === msg.userId) {
          message.nickname = base64UrlSafeToString(msg.nickname);
        }
      });
      // FIXME: 소켓리팩 2차 개선때, 삭제필요 (message Store의 상태값으로만 유지되어야합니다.) (26/1/26)
      updateLegacyMessageNickname(msg);
    }
  };

  // [AL] 참여자 목록을 변경합니다.
  const patchAttendees = (msg: InboundMessageAL) => {
    status.attendees = Object.fromEntries(
      msg.attendees.map((attendee) => [
        attendee.userId,
        {
          userId: attendee.userId,
          isPartnerPerson: attendee.isPartnerPerson === '1',
          nickname: base64UrlSafeToString(attendee.nickname),
          isActive: status.attendees[attendee.userId]?.isActive ?? true,
        },
      ])
    );
  };

  // [CC] 참여자를 추가합니다.
  const updateAttendee = (msg: InboundMessageCC) => {
    status.attendees[msg.userId] = {
      userId: msg.userId,
      nickname: base64UrlSafeToString(msg.nickname),
      isPartnerPerson: msg.isPartnerPerson === '1',
      isActive: true,
    };
  };

  // [CD] 참여자를 삭제합니다.
  const deleteAttendee = (msg: InboundMessageCD) => {
    status.attendees[msg.userId] = {
      ...status.attendees[msg.userId],
      isActive: false,
    };
  };

  return {
    /* state */
    status,
    messages,
    activeAttendees,

    /* actions - messages */
    upsertMessage,
    createMessage,
    editMessage,
    deleteMessage,
    savePastMessage,
    updateMessageNicknames,
    resetMessageStore,

    /* actions - status */
    patchStatus,
    patchAttendees,
    updateAttendee,
    deleteAttendee,
  };
});
